import type { Bien } from "../types/bien";

export type PebStatut = "" | "date_fixee" | "date_passee" | "rapport_en_cours" | "expertise_ok";

/** Statut PEB *calculé* : les états manuels (rapport_en_cours/expertise_ok) sont stockés
 *  dans `statutPeb` ; "date_fixee"/"date_passee" sont dérivés de `pebDatePrevue` (jamais
 *  stockés) → toujours justes partout, sans transition client. */
export function getPebStatut(
  bien: Pick<Bien, "statutPeb" | "pebDatePrevue" | "pebHeure">,
): PebStatut {
  if (bien.statutPeb === "expertise_ok") return "expertise_ok";
  if (bien.statutPeb === "rapport_en_cours") return "rapport_en_cours";
  const dateStr = bien.pebDatePrevue;
  if (!dateStr) return "";
  const dt = bien.pebHeure
    ? new Date(`${dateStr}T${bien.pebHeure}:00`)
    : new Date(`${dateStr}T23:59:59`);
  if (isNaN(dt.getTime())) return "";
  return dt < new Date() ? "date_passee" : "date_fixee";
}

/** Le PEB doit être (re)fait : classe absente, date manquante/invalide/expirée, ou expire <6 mois. */
export function isPebExpiringSoon(pebFinLe?: string, classePeb?: string): boolean {
  if (!classePeb) return true;
  if (!pebFinLe) return true;
  const fin = new Date(pebFinLe);
  if (isNaN(fin.getTime())) return true;
  const now = new Date();
  if (fin < now) return true;
  const sixMonths = new Date();
  sixMonths.setMonth(sixMonths.getMonth() + 6);
  return fin < sixMonths;
}

export type PebValidity = "valid" | "expiring" | "expired";

/**
 * Version 3 états d'`isPebExpiringSoon`, pour un signal visuel distinct entre
 * "encore valide", "expire dans <6 mois" et "expiré/absent" — la pastille PEB
 * du catalogue affichait le même rouge pour ces 2 derniers cas, illisible
 * pour distinguer "à refaire bientôt" de "à refaire d'urgence" (demande
 * Thibaud 2026-08-20). Mêmes seuils qu'`isPebExpiringSoon`, ne le remplace
 * pas : les gates binaires (pipeline ELS, checkElsStates) restent inchangées.
 */
export function pebValidity(pebFinLe?: string, classePeb?: string): PebValidity {
  if (!classePeb || !pebFinLe) return "expired";
  const fin = new Date(pebFinLe);
  if (isNaN(fin.getTime())) return "expired";
  if (fin < new Date()) return "expired";
  const sixMonths = new Date();
  sixMonths.setMonth(sixMonths.getMonth() + 6);
  return fin < sixMonths ? "expiring" : "valid";
}

/** Les 5 champs "nouveau certificat" sont remplis → gate "Expertise terminée". */
export function isPebReportComplete(
  d: Pick<Bien, "nouvClassePeb" | "nouvCertificatPeb" | "nouvPebFinLe" | "nouvConsoKwh" | "nouvEmissionCo2">,
): boolean {
  const ok = (x: unknown) => typeof x === "string" && x.trim().length > 0;
  return ok(d.nouvClassePeb) && ok(d.nouvCertificatPeb) && ok(d.nouvPebFinLe) && ok(d.nouvConsoKwh) && ok(d.nouvEmissionCo2);
}

/** Promotion à "Clôturer l'expertise" (gaté isPebReportComplete) : le nouveau certificat
 *  remplace l'ancien, le staging est vidé, statutPeb → "expertise_ok". Fonction pure.
 *  La date/heure/commentaire de mission sont CONSERVÉS (effacés seulement au passage en
 *  loué, via resetElsCycleForLoue). Champs string vidés en "" (jamais null). */
export function promotePebCycle(bien: Bien): Partial<Bien> {
  return {
    classePeb: bien.nouvClassePeb ?? "",
    certificatPeb: bien.nouvCertificatPeb ?? "",
    pebFinLe: bien.nouvPebFinLe ?? "",
    consoKwh: bien.nouvConsoKwh ?? "",
    emissionCo2: bien.nouvEmissionCo2 ?? "",
    // Optionnels : pas exigés par `isPebReportComplete`. S'ils ont été
    // renseignés pendant le renouvellement on les promeut, sinon on GARDE
    // l'ancienne valeur active (contrairement aux 5 champs ci-dessus,
    // toujours écrasés) — sans ce garde-fou, clôturer un renouvellement sans
    // toucher "Chauffage" effacerait une valeur déjà correcte (demande
    // Thibaud 2026-09-12 — ces 2 champs n'avaient sinon aucun point de
    // saisie pendant un cycle en cours).
    consoTheorique: bien.nouvConsoTheorique || bien.consoTheorique || "",
    chauffageEnergie: bien.nouvChauffageEnergie || bien.chauffageEnergie || "",
    pebCertificatPdf: bien.nouvPebCertificatPdf ?? null,
    nouvClassePeb: "",
    nouvCertificatPeb: "",
    nouvPebFinLe: "",
    nouvConsoKwh: "",
    nouvEmissionCo2: "",
    nouvConsoTheorique: "",
    nouvChauffageEnergie: "",
    nouvPebCertificatPdf: null,
    statutPeb: "expertise_ok",
  };
}
